import { isString, isEmpty, isUndefined, isDefined, isNullish, isTemporal, isInstant, isNumber, isNumeric, RE_COMBINING_MARKS } from '#library/assertion.library.js';
import { ownKeys, ownEntries } from '#library/primitive.library.js';
import { asArray } from '#library/coercion.library.js';
import { pad, singular } from '#library/string.library.js';
import { fromRoman } from '#library/number.library.js';
import { resolveTwoDigitYear, resolveFractionalSeconds } from '#library/temporal.library.js';
import { Enum } from '#library/enumerate.library.js';
import { Match, enums, isTempo, logError, logWarn, logDebug } from '#tempo/support';
const RE_LEADING_DIGITS = /^(\d+)/;
const RE_ERA_BCE = /b\.?c\.?(?:e\.?)?|bc/i;
const RE_ZONE_OFFSET = /^([+-])(\d{1,2})(?::?(\d{2}))?$/;
/** return a new object, with only numeric values */
function num(groups, config) {
    return ownEntries(groups)
        .reduce((acc, [key, val]) => {
        if (isNumber(val)) {
            acc[key] = val;
            return acc;
        }
        const v = isString(val) ? val.trim() : val;
        if (v === '')
            return acc;
        if (isNumeric(v)) {
            acc[key] = Number(v);
            return acc;
        }
        if (config?.registry?.numbers) {
            const low = isString(val) ? val.trim().toLowerCase() : '';
            for (const [k, n] of Object.entries(config.registry.numbers)) {
                if (k.toLowerCase() === low && isNumber(n)) {
                    acc[key] = n;
                    return acc;
                }
            }
        }
        const num = resolveNumber(val, config);
        if (isNumber(num)) {
            acc[key] = num;
            return acc;
        }
        if (Enum.has(enums.NUMBER, num)) {
            acc[key] = enums.NUMBER[num];
            return acc;
        }
        const cal = prefix(val); // get the three-character prefix for a Weekday/Month
        if (Enum.has(enums.WEEKDAY, cal))
            acc[key] = enums.WEEKDAY[cal];
        else if (Enum.has(enums.MONTH, cal))
            acc[key] = enums.MONTH[cal];
        return acc;
    }, {});
}
/**
 * Resolves a number word by matching its prefix against registered number names.
 *
 * @param str - The value to resolve
 * @param config - Optional configuration containing custom registry numbers
 * @returns The matching number name, configured numeric value, or the original value when no match exists
 */
export function resolveNumber(str, config) {
    if (!isString(str))
        return str;
    const low = str.trim().toLowerCase();
    if (config?.registry?.numbers) {
        for (const [k, v] of Object.entries(config.registry.numbers)) {
            if (k.toLowerCase() === low && isNumber(v))
                return v;
        }
    }
    return Enum.keys(enums.NUMBER).find((key) => key.startsWith(low)) ?? str;
}
/**
 * Resolves an ordinal expression to a numeric position.
 *
 * @param str - An ordinal expression, numeric value, or `last`
 * @param config - Optional configuration containing custom registry ordinals
 * @returns The 1-based position, `-1` for `last`, or `1` when the input is invalid or unspecified
 */
export function resolveNth(str, config) {
    if (isNullish(str))
        return 1;
    if (!isString(str)) {
        const num = Number(str);
        return Number.isFinite(num) ? num : 1;
    }
    const low = str.trim().toLowerCase();
    if (config?.registry?.ordinals) {
        for (const [k, v] of Object.entries(config.registry.ordinals)) {
            if (k.toLowerCase() === low && isNumber(v))
                return v;
        }
    }
    const ordValue = enums.ORDINAL[low];
    if (isNumber(ordValue))
        return ordValue;
    const match = low.match(RE_LEADING_DIGITS);
    if (match) {
        const val = parseInt(match[1], 10);
        if (Number.isFinite(val))
            return val;
    }
    return 1;
}
/**
 * Clears specified keys and their alternative variants from regex capture groups.
 *
 * @param groups - The groups object containing regex capture results
 * @param baseKeys - Base key names to clear along with their `_alt` suffixed variants
 */
export function clearGroupKeys(groups, ...baseKeys) {
    for (const key of ownKeys(groups)) {
        for (const base of baseKeys) {
            if (key === base || key.startsWith(`${base}_alt`))
                delete groups[key];
        }
    }
}
/** implementation */
export function prefix(str) {
    if (!isString(str))
        return str;
    const low = str.trim().toLowerCase().substring(0, 3);
    if (low.length < 2)
        return str; // cannot determine ambiguity with less than 3 characters
    if (low === 'all' || low === 'eve')
        return 'All'; // handle special case of "all" / "every"
    for (const table of [enums.WEEKDAY, enums.MONTH]) {
        const match = Enum.keys(table).find(key => {
            const normalized = key.toLowerCase();
            return normalized.startsWith(low);
        });
        if (match)
            return match;
    }
    return str;
}
/** resolve a relative modifier (+, -, <, >, =, etc) */
export function normalizeModifier(mod, config = {}) {
    if (mod && config?.registry?.modifiers) {
        const norm = mod.toLowerCase().trim().normalize('NFD').replace(RE_COMBINING_MARKS, "");
        for (const [sym, words] of Object.entries(config.registry.modifiers)) {
            if (asArray(words)
                .map(w => w.toLowerCase().trim().normalize('NFD').replace(RE_COMBINING_MARKS, ""))
                .includes(norm)) {
                return sym;
            }
        }
    }
    return mod;
}
/**
 * Calculates a signed adjustment from a modifier and period position.
 *
 * @param mod - The modifier that determines the adjustment direction and comparison behavior
 * @param adjust - The magnitude of the adjustment
 * @param offset - The reference period position
 * @param period - The current period position
 * @returns The signed adjustment to apply
 */
export function parseModifier({ mod, adjust, offset, period }, config = {}) {
    adjust = Math.abs(adjust);
    mod = normalizeModifier(mod, config);
    switch (mod) {
        case undefined:
        case '=':
            return 0;
        case '+':
            return adjust;
        case '-':
            return -adjust;
        case '<':
            return (period <= offset) ? -adjust : -(adjust - 1);
        case '<=':
        case '-=':
            return (period < offset) ? -adjust : -(adjust - 1);
        case '>':
            return (period >= offset) ? adjust : (adjust - 1);
        case '>=':
        case '+=':
            return (period > offset) ? adjust : (adjust - 1);
        default:
            return 0;
    }
}
/**
 * Resolves an ordinal weekday within a specified or anchored month and year.
 *
 * Consumed ordinal, weekday, and date capture groups are removed from `groups`.
 *
 * @param groups - The parsed input's mutable capture groups.
 * @param wkd - The weekday name or identifier.
 * @param nthStr - The ordinal position, such as `3rd`, `first`, or `last`.
 * @param dateTime - The date and time used as the resolution anchor.
 * @param config - Tempo configuration options.
 * @returns The resolved date and time, the anchor date and time when the ordinal falls outside the month, or `undefined` when the weekday or date components cannot be resolved.
 */
export function parseOrdinalWeekday(groups, wkd, nthStr, dateTime, config) {
    const nthVal = resolveNth(nthStr, config);
    const weekday = prefix(wkd);
    const targetWkd = Enum.get(enums.WEEKDAY, weekday) ?? Enum.get(enums.WEEKDAYS, weekday);
    if (!isNumber(targetWkd))
        return undefined;
    let fallbackAnchor = config?.anchor;
    if (isTempo(fallbackAnchor))
        fallbackAnchor = fallbackAnchor.toDateTime();
    if (isInstant(fallbackAnchor))
        fallbackAnchor = fallbackAnchor.toZonedDateTimeISO(config?.timeZone || 'UTC');
    if (!isTemporal(fallbackAnchor))
        fallbackAnchor = undefined;
    let yy = groups.yy ? Number(groups.yy) : (fallbackAnchor?.year ?? dateTime.year);
    let mm = groups.mm ? num({ month: groups.mm }, config).month : (fallbackAnchor?.month ?? dateTime.month);
    if (Number.isFinite(yy) && yy < 100)
        yy = resolveTwoDigitYear(yy, config?.pivot, dateTime);
    if (!Number.isFinite(yy) || !Number.isFinite(mm))
        return undefined;
    if (nthVal === 0 || Math.abs(nthVal) > 5) {
        logError(`Ordinal weekday out of bounds for month ${mm}`, config);
        return undefined;
    }
    const firstDay = Temporal.PlainDate.from({ year: yy, month: mm, day: 1 });
    let targetDate;
    if (nthVal < 0) {
        const lastDay = firstDay.add({ months: 1 }).subtract({ days: 1 });
        const daysBack = (lastDay.dayOfWeek - targetWkd + 7) % 7;
        const lastWkd = lastDay.subtract({ days: daysBack });
        targetDate = lastWkd.subtract({ weeks: Math.abs(nthVal) - 1 });
    }
    else {
        const daysUntil = (targetWkd - firstDay.dayOfWeek + 7) % 7;
        const firstWkd = firstDay.add({ days: daysUntil });
        targetDate = firstWkd.add({ weeks: nthVal - 1 });
    }
    clearGroupKeys(groups, "ord", "nth", "wkd", "mm", "yy", "mod", "nbr", "sfx", "afx", "unt", "dd");
    if (targetDate.year === yy && targetDate.month === mm) {
        const tz = dateTime.timeZoneId ?? dateTime.timeZone ?? 'UTC';
        const resZdt = targetDate.toZonedDateTime(tz).withPlainTime(dateTime.toPlainTime());
        logDebug(`[Lexer] Resolved ordinal weekday to ${targetDate.toString()}`, config);
        return resZdt;
    }
    else {
        logError(`Ordinal weekday out of bounds for month ${mm}`, config);
        return dateTime;
    }
}
/**
 * Resolves weekday captures relative to a date and time.
 *
 * Supports ordinal weekdays, relative modifiers, numeric week offsets, and weekday suffixes while preserving the time and timezone.
 * When both `ord` and `nth` ordinal captures are present, `ord` takes precedence.
 *
 * @param groups - Lexer capture groups containing the weekday expression
 * @param dateTime - Date and time used as the reference point
 * @param config - Tempo configuration used for ordinal anchors and modifier aliases
 * @returns The date and time adjusted to the resolved weekday, or the original value when the captures are invalid or unsupported
 */
export function parseWeekday(groups, dateTime, config) {
    const { wkd, mod, nbr = '1', sfx, afx, ...rest } = groups;
    if (isUndefined(wkd))
        return dateTime;
    const nthStr = groups["ord"] ?? groups["nth"] ?? (groups["mod"]?.toLowerCase() === 'last' && (groups["mm"] || groups["yy"]) ? 'last' : undefined);
    if (isDefined(nthStr)) {
        const res = parseOrdinalWeekday(groups, wkd, nthStr, dateTime, config);
        if (isDefined(res))
            return res;
    }
    const time = ['hh', 'mi', 'ss', 'ms', 'us', 'ns', 'ff', 'mer'];
    if (!ownKeys(rest).every(key => time.includes(key) || key.startsWith('per')))
        return dateTime;
    if (!isEmpty(mod) && !isEmpty(sfx)) {
        logWarn(`Cannot provide both a modifier '${mod}' and suffix '${sfx}'`, config);
        return dateTime;
    }
    const weekday = prefix(wkd);
    const { nbr: adjust = 1 } = num({ nbr }, config);
    const offset = Enum.get(enums.WEEKDAY, weekday) ?? Enum.get(enums.WEEKDAYS, weekday);
    if (!isNumber(offset)) {
        logError(`Invalid weekday token: "${wkd}"`, config);
        return dateTime;
    }
    const days = offset - dateTime.dayOfWeek
        + (parseModifier({ mod: mod ?? sfx ?? afx, adjust, offset, period: dateTime.dayOfWeek }, config) * dateTime.daysInWeek);
    delete groups["wkd"];
    delete groups["mod"];
    delete groups["nbr"];
    delete groups["sfx"];
    const finalDateTime = dateTime.add({ days });
    logDebug(`[Lexer] Applied weekday offset of ${days} days`, config);
    return finalDateTime;
}
/**
 * Resolves an ordinal date expression to a specific date.
 *
 * Consumed ordinal and date capture groups are removed from `groups`.
 *
 * @param groups - Mutable regex capture groups from the parsed input
 * @param unt - Time unit targeted by the ordinal expression
 * @param nthStr - Ordinal position, such as `1st` or `last`
 * @param dateTime - Date and time providing the resolution context
 * @param config - Tempo configuration used for diagnostics
 * @param year - Target year
 * @param month - Target month
 * @returns The resolved zoned date-time, or `undefined` if the ordinal date is invalid
 */
export function parseOrdinalDate(groups, unt, nthStr, dateTime, config, year, month) {
    const nthVal = resolveNth(nthStr, config);
    const untStr = unt ? singular(unt).toLowerCase() : 'day';
    let targetYear = year;
    let targetMonth = isDefined(groups.mm) ? month : undefined;
    let targetDate;
    if (untStr === 'day') {
        const maxDays = isDefined(targetMonth) ? 31 : 366;
        if (nthVal === 0 || Math.abs(nthVal) > maxDays) {
            logError(`Ordinal date day ${nthVal} out of bounds for ${isDefined(targetMonth) ? `month ${targetMonth}` : `year ${targetYear}`}`, config);
            return undefined;
        }
        if (nthVal < 0) {
            const offsetFromEnd = Math.abs(nthVal) - 1;
            if (isDefined(targetMonth)) {
                const firstDay = Temporal.PlainDate.from({ year: targetYear, month: targetMonth, day: 1 });
                const lastDay = firstDay.add({ months: 1 }).subtract({ days: 1 });
                targetDate = lastDay.subtract({ days: offsetFromEnd });
            }
            else {
                const lastDay = Temporal.PlainDate.from({ year: targetYear, month: 12, day: 31 });
                targetDate = lastDay.subtract({ days: offsetFromEnd });
            }
        }
        else if (nthVal > 0) {
            if (isDefined(targetMonth)) {
                const firstDay = Temporal.PlainDate.from({ year: targetYear, month: targetMonth, day: 1 });
                targetDate = firstDay.add({ days: nthVal - 1 });
            }
            else {
                const startOfYear = Temporal.PlainDate.from({ year: targetYear, month: 1, day: 1 });
                targetDate = startOfYear.add({ days: nthVal - 1 });
            }
        }
    }
    if (targetDate) {
        if (isDefined(targetMonth) && targetDate.month !== targetMonth) {
            logError(`Ordinal date day ${nthVal} out of bounds for month ${targetMonth}`, config);
            return undefined;
        }
        if (targetDate.year !== targetYear) {
            logError(`Ordinal date day ${nthVal} out of bounds for year ${targetYear}`, config);
            return undefined;
        }
        clearGroupKeys(groups, "ord", "nth", "unt", "mm", "yy", "yy2", "dd", "mod", "nbr", "afx");
        const tz = dateTime.timeZoneId ?? dateTime.timeZone ?? 'UTC';
        const resZdt = targetDate.toZonedDateTime(tz).withPlainTime(dateTime.toPlainTime());
        logDebug(`[Lexer] Resolved ordinal date to ${targetDate.toString()}`, config);
        return resZdt;
    }
    return undefined;
}
/**
 * Resolves date, relative-unit, and ordinal captures against a reference date.
 *
 * When both `ord` and `nth` ordinal captures are present, `ord` takes precedence. The function removes consumed captures or replaces them with normalized year, month, and day values.
 * Roman month/year captures are normalized to numbers; a Roman year without month or day defaults to January 1.
 *
 * @param groups - Mutable lexer capture groups containing the date expression.
 * @param dateTime - Date and time used as the resolution context.
 * @param config - Tempo configuration used for anchors, modifiers, and two-digit-year resolution.
 * @param pivot - Fallback pivot for resolving two-digit years.
 * @returns The resolved date and time, or the original value for unsupported captures or handled validation failures.
 * @throws Errors reported through the configured error boundary unless `config.catch` is enabled;
 * Temporal errors from date resolution propagate regardless of that setting.
 */
export function parseDate(groups, dateTime, config, pivot) {
    const { mod, nbr = '1', afx, unt, era } = groups;
    if (groups.Rm) {
        const parsedMonth = fromRoman(groups.Rm);
        if (Number.isFinite(parsedMonth))
            groups.mm = String(parsedMonth);
        delete groups.Rm;
    }
    if (groups.Ry) {
        const parsedYear = fromRoman(groups.Ry);
        if (Number.isFinite(parsedYear)) {
            groups.yy = String(parsedYear);
            if (isUndefined(groups.mm) && isUndefined(groups.dd)) {
                groups.mm = '1';
                groups.dd = '1';
            }
        }
        delete groups.Ry;
    }
    let yy = groups.yy || undefined;
    if (isUndefined(yy) && isDefined(groups.yy2)) {
        let yy2Num = parseInt(groups.yy2, 10);
        if (Number.isFinite(yy2Num)) {
            if (yy2Num < 100)
                yy2Num = resolveTwoDigitYear(yy2Num, config?.pivot ?? pivot, dateTime);
            yy = String(yy2Num);
        }
    }
    let { mm, dd } = groups;
    if (era) {
        if (isUndefined(yy)) {
            logError(`[Tempo#lexer] Cannot resolve era '${era}' without an explicit year`, config);
            return dateTime;
        }
        const isBCE = RE_ERA_BCE.test(era);
        if (isBCE)
            yy = String(-(Number(yy) - 1));
        if (isUndefined(mm) && isUndefined(dd)) {
            mm = '1';
            dd = '1';
        }
        delete groups["era"];
    }
    if (isEmpty(yy) && isEmpty(mm) && isEmpty(dd) && isUndefined(unt) && isUndefined(groups["ord"] ?? groups["nth"]))
        return dateTime;
    if (!isEmpty(mod) && !isEmpty(afx)) {
        logWarn(`A date unit cannot have both a leading modifier ('${mod}') and a trailing affix ('${afx}') — use one or the other`, config);
        return dateTime;
    }
    // Fallback order: provided -> config.anchor (normalized to Temporal-like) -> dateTime
    let anchor = config?.anchor;
    if (isTempo(anchor))
        anchor = anchor.toDateTime();
    if (isInstant(anchor))
        anchor = anchor.toZonedDateTimeISO(config?.timeZone || 'UTC');
    if (!isTemporal(anchor))
        anchor = undefined;
    const fallbackYear = isDefined(anchor?.year) ? anchor.year : dateTime.year;
    const fallbackMonth = isDefined(anchor?.month) ? anchor.month : dateTime.month;
    const fallbackDay = isDefined(anchor?.day) ? anchor.day : dateTime.day;
    let { year, month, day } = num({
        year: yy ?? fallbackYear,
        month: mm ?? fallbackMonth,
        day: dd ?? fallbackDay,
    }, config);
    const isExplicitNth = isDefined(groups["ord"] ?? groups["nth"]);
    const nthStr = groups["ord"] ?? groups["nth"] ?? (groups["mod"]?.toLowerCase() === 'last' && (groups["mm"] || groups["yy"] || unt) ? 'last' : undefined);
    if (isDefined(nthStr)) {
        const res = parseOrdinalDate(groups, unt, nthStr, dateTime, config, year, month);
        if (isDefined(res))
            return res;
        if (isExplicitNth)
            return dateTime;
    }
    if (unt) {
        const { nbr: adjust = 1 } = num({ nbr }, config);
        const dir = parseModifier({ mod: mod ?? afx, adjust: 1, offset: 0, period: 0 }, config);
        const direction = dir < 0 ? -1 : 1;
        const plural = `${singular(unt)}s`;
        dateTime = dateTime.add({ [plural]: adjust * direction });
        delete groups["unt"];
        delete groups["nbr"];
        delete groups["afx"];
        delete groups["mod"];
        return dateTime;
    }
    if (year.toString().match(Match.twoDigit))
        year = resolveTwoDigitYear(year, config?.pivot ?? pivot, dateTime);
    const { nbr: adjust = 1 } = num({ nbr }, config);
    const offset = Number(pad(month) + '.' + pad(day));
    const period = Number(pad(dateTime.month) + '.' + pad(dateTime.day + 1));
    const tz = dateTime.timeZoneId ?? dateTime.timeZone;
    const pmRes = parseModifier({ mod: mod ?? afx, adjust, offset, period }, config);
    year += pmRes;
    Object.assign(groups, { yy: year, mm: month, dd: day });
    delete groups["mod"];
    delete groups["nbr"];
    delete groups["afx"];
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
        logError(`Invalid Date components: year=${year}, month=${month}, day=${day}`, config);
        return dateTime;
    }
    const finalDateTime = Temporal.PlainDate.from({ year, month, day }, { overflow: 'constrain' })
        .toZonedDateTime(tz)
        .withPlainTime(dateTime.toPlainTime());
    logDebug(`[Lexer] Resolved Date components to ${year}-${month}-${day}`, config);
    return finalDateTime;
}
/** resolve a time pattern match */
export function parseTime(groups = {}, dateTime, config) {
    if (isUndefined(groups["hh"]))
        return dateTime;
    let { hh = 0, mi = 0, ss = 0, ms = 0, us = 0, ns = 0 } = num(groups, config);
    if (hh >= 24) {
        dateTime = dateTime.add({ days: Math.trunc(hh / 24) });
        hh %= 24;
    }
    if (isDefined(groups["ff"])) {
        const sub = resolveFractionalSeconds(groups["ff"]);
        ms = sub.millisecond;
        us = sub.microsecond;
        ns = sub.nanosecond;
    }
    if (groups["mer"]?.toLowerCase() === 'pm' && hh < 12 && (hh + mi + ss + ms + us + ns) > 0)
        hh += 12;
    if (groups["mer"]?.toLowerCase() === 'am' && hh >= 12)
        hh -= 12;
    const finalDateTime = dateTime.withPlainTime({ hour: hh, minute: mi, second: ss, millisecond: ms, microsecond: us, nanosecond: ns });
    logDebug(`[Lexer] Resolved Time components to ${pad(hh)}:${pad(mi)}:${pad(ss)}`, undefined);
    return finalDateTime;
}
/**
 * Applies captured timezone and calendar values to a zoned date-time.
 *
 * The canonical `tz` capture takes precedence over `tzd`, bracketed zones take precedence over both, and `Z` is normalized to `UTC`. Newly applied values update `config`, and successful processing removes the zone and calendar captures from `groups`. Invalid timezone identifiers are ignored.
 *
 * @param groups - Mutable lexer capture groups containing zone or calendar values.
 * @param dateTime - Date and time to update.
 * @param config - Optional Tempo configuration updated with applied values.
 * @returns The date and time with valid zone and calendar adjustments applied.
 */
export function parseZone(groups, dateTime, config) {
    if (!isTemporal(dateTime))
        return dateTime;
    const tzd = (groups["tz"] ?? groups["tzd"])?.replace(Match.zed, 'UTC');
    const brk = groups["brk"]?.replace(Match.zed, 'UTC');
    let zone = brk || tzd;
    if (zone) {
        const match = zone.match(RE_ZONE_OFFSET);
        if (match)
            zone = `${match[1]}${match[2].padStart(2, '0')}:${match[3] ?? '00'}`;
    }
    let cal = groups["cal"];
    if (zone?.startsWith('u-ca=')) {
        cal = zone;
        zone = tzd;
    }
    const zdt = dateTime;
    if (zone && zone !== zdt.timeZoneId) {
        const resolvedZone = enums.TIMEZONE[zone.toLowerCase()] ?? zone;
        try {
            dateTime = zdt.toPlainDateTime().toZonedDateTime(resolvedZone);
            if (config)
                config.timeZone = resolvedZone;
        }
        catch {
            logWarn(`Unrecognized or invalid timezone identifier: '${zone}'`, config);
        }
    }
    if (cal && cal !== dateTime.calendarId) {
        const calendar = cal.startsWith('u-ca=') ? cal.substring(5) : cal;
        if (config)
            config.calendar = calendar;
        dateTime = dateTime.withCalendar(calendar);
    }
    delete groups["brk"];
    delete groups["cal"];
    delete groups["tzd"];
    delete groups["tz"];
    if (zone || cal)
        logDebug(`[Lexer] Applied Zone/Calendar adjustments: Zone=${zone ?? 'unchanged'}, Calendar=${cal ?? 'unchanged'}`, config);
    return dateTime;
}
